import React, {useContext, useEffect, useRef, useState} from 'react';
import FilterGroup from './FilterGroup.jsx';
import {Constants} from '../../view-constants';
import {articleOverviewStaticContext} from '../../ArticleOverviewStatic.jsx';
import {useTrinity} from '../../hooks/use-trinity.js';
import Landmark from '@core/scripts/components/landmark-adapter';
import {useBreakpointUp} from "bonprix-design-system";

function FilterGroups({
    shopIdFilterGroup,
    filterGroups,
    hasTooManyFilterGroupsForInitialDisplay,
    hits,
    isSticky,
    filterSubmit,
    filterViewValue,
}) {
    const {texts, shopId} = useContext(articleOverviewStaticContext);
    const breakpointXlUp = useBreakpointUp('xl');
    const [isExpanded, setIsExpanded] = useState(false);
    const [stickyLimitation, setStickyLimitation] = useState(false);
    const filterGroupsWrapper = useRef(null);
    const numberOfFilterGroups = filterGroups.length;
    const prevVisibleFilterGroups = useRef(defaultVisibleFilterGroups());
    const {sendFilterView} = useTrinity();

    useEffect(() => {
        if (filterViewValue) {
            sendFilterView('filter_drop_down', filterViewValue, Constants.FILTER_VIEW_EVENT_VERSION_TESTGROUP);
            const landmarkData = {
                filter_view: filterViewValue,
                filter_view_shopId: shopId.current,
            };
            Landmark.sendActionLandmark(landmarkData);
        }
    }, []);

    useEffect(() => {
        if (filterGroupsWrapper) {
            if (isSticky && breakpointXlUp) {
                const openFilter = filterGroupsWrapper.current.querySelector('[jsh-dropdown-content]');
                if (!openFilter) {
                    setIsExpanded(false);
                    setStickyLimitation(true);
                }
            } else {
                setStickyLimitation(false);
            }
        }
    }, [isSticky]);

    const numberOfVisibleFilterGroups = getNumberOfVisibleFilterGroups();
    const visibleFilterGroups = filterGroups.slice(0, numberOfVisibleFilterGroups);

    const stickyLimitationNeeded = filterGroups.length > Constants.MAX_NUMBER_OF_FILTER_GROUPS_FOR_STICKY;
    const expansionTogglerNeeded = hasTooManyFilterGroupsForInitialDisplay || (isSticky && stickyLimitationNeeded);

    return (
        <div className="filter__groups -large" ref={filterGroupsWrapper}>
            {visibleFilterGroups.map((filterGroup, index) => {
                return (
                    <div
                        key={index}
                        className="filter__group-wrapper"
                        jsh-filter-group-wrapper=""
                        test-ref__filter-group={filterGroup.name}
                    >
                        <FilterGroup
                            shopIdFilterGroup={shopIdFilterGroup}
                            hits={hits}
                            expandLeft={index % 4 > 1} // each rows 1./2. FilterGroup should expand to right, 3./4. to left
                            filterGroup={filterGroup}
                            preventFilterUpdate={true}
                            isSticky={isSticky}
                            filterSubmit={filterSubmit}
                        />
                    </div>
                );
            })}

            {expansionTogglerNeeded ? renderExpansionToggler() : <></>}
        </div>
    );

    function renderExpansionToggler() {
        const linkClasses = isExpanded ? 'shrink-link' : 'expand-link';
        return (
            <div className="filter__group-wrapper expansion-toggler">
                <span className={linkClasses} onClick={toggleFilterGroupWrappersExpansion}>
                    {isExpanded ? texts.filter_showLess : texts.filter_showMore}
                </span>
            </div>
        );
    }

    function defaultVisibleFilterGroups() {
        return hasTooManyFilterGroupsForInitialDisplay
            ? Constants.MAX_NUMBER_OF_FILTER_GROUPS_FOR_INITIAL_DISPLAY - 1
            : numberOfFilterGroups;
    }

    function getNumberOfVisibleFilterGroups() {
        if (isSticky && !isExpanded && !stickyLimitation) {
            return prevVisibleFilterGroups.current;
        }

        const stickyLimitationNeeded = numberOfFilterGroups > Constants.MAX_NUMBER_OF_FILTER_GROUPS_FOR_STICKY;

        if (isExpanded) {
            return numberOfFilterGroups;
        } else {
            if (isSticky) {
                return stickyLimitationNeeded
                    ? Constants.MAX_NUMBER_OF_FILTER_GROUPS_FOR_STICKY - 1
                    : numberOfFilterGroups;
            } else {
                return defaultVisibleFilterGroups();
            }
        }
    }

    function toggleFilterGroupWrappersExpansion(event) {
        if (isSticky) {
            setStickyLimitation(true);
        }
        event.preventDefault();
        setIsExpanded(!isExpanded);
    }

    function deactivateFilterGroupWrappersExpansion() {
        setIsExpanded(false);
        setStickyLimitation(true);
    }

    function deactivateStickyLimitation() {
        setStickyLimitation(false);
    }
}

export default React.memo(FilterGroups);
